import type React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import type { MessageDescriptor } from 'react-intl';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexRow } from 'ui-components/components/flex';

import type { Currency } from '../../../prop-types/currency-types';
import { CustomsField } from './customs-field';

import commonStyle from './common-style.module.less';

interface CustomsCurrencyInputProps {
  currencyInputLabel: MessageDescriptor;
  currencyInputCode?: string;
  initialCurrencyInputValue?: number | string | null;
  updateCurrencyValue: (currency: Currency) => void;
}

export const CustomsCurrencyInput: React.FC<CustomsCurrencyInputProps> = ({
  currencyInputCode = '',
  currencyInputLabel,
  initialCurrencyInputValue,
  updateCurrencyValue,
}) => {
  const [currencyInputValue, setCurrencyInputValue] = useState(initialCurrencyInputValue);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    setCurrencyInputValue(initialCurrencyInputValue);
  }, [setCurrencyInputValue, initialCurrencyInputValue]);

  const onValueChange = useCallback(
    (updatedCurrencyValue: number | string | undefined | null) => {
      setCurrencyInputValue(updatedCurrencyValue);
    },
    [setCurrencyInputValue],
  );

  const onBlur = useCallback(() => {
    isFocusedRef.current = false;
    updateCurrencyValue({
      value: Number(currencyInputValue),
      code: currencyInputCode,
    });
  }, [currencyInputValue, updateCurrencyValue, currencyInputCode]);

  const onFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  return (
    <CustomsField label={currencyInputLabel} recommended>
      <FlexRow>
        <CurrencyInput
          className={commonStyle['currency-input']}
          currencyCode={currencyInputCode}
          data-testid='customs-amount'
          onBlur={onBlur}
          onChange={onValueChange}
          onFocus={onFocus}
          value={currencyInputValue || ''}
        />
        <div className={commonStyle['vert-center']}>
          <span className={commonStyle['currency-code']}>{currencyInputCode}</span>
        </div>
      </FlexRow>
    </CustomsField>
  );
};
